import React, { useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
function CircuitBackground() {
  return <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" style={{
    opacity: 0.04
  }} data-id="element-173">
      <defs data-id="element-174">
        <pattern id="circuit-hero" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse" data-id="element-175">
          <path d="M0 60 H50 V30 H70 V90 H120" fill="none" stroke="#C4C4CC" strokeWidth="0.5" data-id="element-176" />
          <path d="M60 0 V40 H90 V80 H60 V120" fill="none" stroke="#C4C4CC" strokeWidth="0.5" data-id="element-177" />
          <circle cx="50" cy="60" r="2" fill="#C4C4CC" data-id="element-178" />
          <circle cx="70" cy="30" r="2" fill="#C4C4CC" data-id="element-179" />
          <circle cx="70" cy="90" r="2" fill="#C4C4CC" data-id="element-180" />
          <circle cx="60" cy="40" r="2" fill="#C4C4CC" data-id="element-181" />
          <circle cx="90" cy="40" r="2" fill="#C4C4CC" data-id="element-182" />
          <circle cx="90" cy="80" r="2" fill="#C4C4CC" data-id="element-183" />
          <circle cx="60" cy="80" r="2" fill="#C4C4CC" data-id="element-184" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#circuit-hero)" data-id="element-185" />
    </svg>;
}
function CircuitTraceLine({
  delay
}: {
  delay: number;
}) {
  return <svg className="w-48 md:w-72 h-6 my-1" viewBox="0 0 288 24" fill="none" data-id="element-186">
      <motion.path d="M0 12 H80 V4 H160 V20 H240 V12 H288" stroke="#FF2D2D" strokeWidth="1" fill="none" initial={{
      pathLength: 0,
      opacity: 0
    }} animate={{
      pathLength: 1,
      opacity: 1
    }} transition={{
      duration: 1.2,
      delay,
      ease: 'easeInOut'
    }} data-id="element-187" />
      <motion.circle cx="80" cy="12" r="2" fill="#FF2D2D" initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 1, 0.5, 1]
    }} transition={{
      duration: 0.5,
      delay: delay + 0.4
    }} data-id="element-188" />
      <motion.circle cx="160" cy="4" r="2" fill="#FF2D2D" initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 1, 0.5, 1]
    }} transition={{
      duration: 0.5,
      delay: delay + 0.7
    }} data-id="element-189" />
      <motion.circle cx="240" cy="20" r="2" fill="#FF2D2D" initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 1, 0.5, 1]
    }} transition={{
      duration: 0.5,
      delay: delay + 1.0
    }} data-id="element-190" />
    </svg>;
}
interface CounterStatProps {
  target: number;
  label: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
}
function CounterStat({
  target,
  label,
  prefix = '',
  suffix = '',
  decimals = 0
}: CounterStatProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.5
  });
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => decimals > 0 ? v.toFixed(decimals) : Math.floor(v).toLocaleString());
  useEffect(() => {
    if (isInView) {
      animate(count, target, {
        duration: 2.5,
        ease: 'easeOut'
      });
    }
  }, [isInView, target, count]);
  return <div ref={ref} className="flex flex-col items-center px-4 md:px-8" data-id="element-191">
      <div className="flex items-baseline" data-id="element-192">
        {prefix && <span className="font-['Bebas_Neue'] text-3xl md:text-5xl text-[#C4C4CC]" data-id="element-193">
            {prefix}
          </span>}
        <motion.span className="font-['Bebas_Neue'] text-3xl md:text-5xl text-[#C4C4CC]" data-id="element-194">
          {rounded}
        </motion.span>
        {suffix && <span className="font-['Bebas_Neue'] text-3xl md:text-5xl text-[#C4C4CC]" data-id="element-195">
            {suffix}
          </span>}
      </div>
      <span className="text-xs md:text-sm text-[#8A8A96] mt-1 tracking-wider uppercase font-light" data-id="element-196">
        {label}
      </span>
    </div>;
}
const headlineWords = ['DECODE.', 'MATCH.', 'AMPLIFY.'];
export function Hero() {
  return <section className="relative min-h-screen w-full flex flex-col bg-[#050508] overflow-hidden" data-id="element-197">
      <CircuitBackground data-id="element-198" />

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050508] via-transparent to-[#050508] pointer-events-none" data-id="element-199" />
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#050508] to-transparent pointer-events-none" data-id="element-200" />

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6" data-id="element-201">
        <span className="font-['Bebas_Neue'] text-2xl md:text-3xl tracking-[0.25em] text-[#C4C4CC]" data-id="element-202">
          ARCADEUROPE
        </span>
        <div className="hidden md:flex items-center gap-8" data-id="element-203">
          <a href="#protocol" className="text-sm text-[#8A8A96] hover:text-[#FF2D2D] transition-colors tracking-wider uppercase" data-id="element-204">
            Protocol
          </a>
          <a href="#modules" className="text-sm text-[#8A8A96] hover:text-[#FF2D2D] transition-colors tracking-wider uppercase" data-id="element-205">
            Modules
          </a>
          <a href="#deploy" className="text-sm text-[#8A8A96] hover:text-[#FF2D2D] transition-colors tracking-wider uppercase" data-id="element-206">
            Deploy
          </a>
        </div>
      </nav>

      {/* Main hero content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6" data-id="element-207">
        {/* Headline */}
        <div className="flex flex-col items-center" data-id="element-208">
          {headlineWords.map((word, i) => <div key={word} className="flex flex-col items-center" data-id="element-209">
              <motion.h1 className="font-['Bebas_Neue'] text-7xl sm:text-8xl md:text-9xl lg:text-[10rem] tracking-wider text-[#C4C4CC] leading-[0.85]" initial={{
            y: 60,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            duration: 0.7,
            delay: i * 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-210">
                {word}
              </motion.h1>
              {i < headlineWords.length - 1 && <CircuitTraceLine delay={0.6 + i * 0.3} data-id="element-211" />}
            </div>)}
        </div>

        {/* Descriptor */}
        <motion.p className="mt-8 text-base md:text-lg text-[#8A8A96] text-center max-w-xl leading-relaxed font-light" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 1.0
      }} data-id="element-212">
          AI-powered game-to-player marketplace. Precision matching at
          continental scale.
        </motion.p>

        {/* CTA */}
        <motion.a href="#deploy" className="mt-8 inline-flex items-center gap-2 px-8 py-3.5 bg-[#FF2D2D] text-white font-medium text-sm tracking-wider uppercase rounded-none hover:bg-[#CC1A1A] transition-colors" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 1.2
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} data-id="element-213">
          Request Access
          <ArrowRightIcon className="w-4 h-4" data-id="element-214" />
        </motion.a>
      </div>

      {/* Counter stats */}
      <motion.div className="relative z-10 flex flex-wrap justify-center items-center gap-0 pb-12 md:pb-16" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 1.5
    }} data-id="element-215">
        <CounterStat target={12000} suffix="+" label="Games Indexed" data-id="element-216" />
        <div className="hidden md:block w-px h-12 bg-[#8A8A96] opacity-30" data-id="element-217" />
        <CounterStat target={340} suffix="+" label="Operators Connected" data-id="element-218" />
        <div className="hidden md:block w-px h-12 bg-[#8A8A96] opacity-30" data-id="element-219" />
        <CounterStat target={98.2} suffix="%" label="Match Accuracy" decimals={1} data-id="element-220" />
        <div className="hidden md:block w-px h-12 bg-[#8A8A96] opacity-30" data-id="element-221" />
        <CounterStat target={47} prefix="€" suffix="M+" label="Revenue Attributed" data-id="element-222" />
      </motion.div>
    </section>;
}